import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Sidebar, { MobileMenuButton } from "./Sidebar";
import Topbar from "./Topbar";
import GuidedTour from "./GuidedTour";
import PasswordSetupPrompt from "./PasswordSetupPrompt";
import AccessExpired from "../pages/AccessExpired";
import { useAuth } from "../context/AuthContext";
import { useQuery } from "../lib/useQuery";
import { supabase } from "../lib/supabase";
import { getStudent, markTourSeen, markPasswordSet } from "../lib/queries";

const PAGE_META = {
  "/": { title: "Panel Principal", subtitle: "Resumen de tu progreso" },
  "/actividades": { title: "Mis Actividades", subtitle: "Tareas y seguimiento del programa" },
  "/herramientas": { title: "Herramientas", subtitle: "Simulador de rentabilidad" },
  "/documentos": { title: "Documentos", subtitle: "Contrato y facturas" },
  "/pagos": { title: "Pagos y Facturación", subtitle: "Historial de pagos" },
  "/sesiones": { title: "Sesiones", subtitle: "Coaching y llamadas" },
  "/emergencia": { title: "Llamada de Emergencia", subtitle: "Reconduce tu situación y retoma el rumbo" },
  "/perfil": { title: "Mi Perfil", subtitle: "Información personal" },
};

export default function Layout() {
  const location = useLocation();
  const meta = PAGE_META[location.pathname] || PAGE_META["/"];
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const { user } = useAuth();
  const { data: student, refetch } = useQuery(() => getStudent(user.id), [user.id]);
  const [tourDismissed, setTourDismissed] = useState(false);
  const [passwordDismissed, setPasswordDismissed] = useState(false);

  // Gate de acceso con caducidad. Usamos la MISMA función que la RLS
  // (has_active_access), que aplica auth.uid() y respeta el kill-switch:
  // con el flag apagado devuelve true SIEMPRE, así que el gate nunca bloquea.
  // Fail-open: si la RPC falla (red, etc.) dejamos entrar y registramos el
  // error; el corte robusto vive en la RLS.
  const { data: hasAccess, loading: accessLoading } = useQuery(async () => {
    const { data, error } = await supabase.rpc("has_active_access");
    if (error) {
      console.error("[has_active_access] RPC falló, fail-open:", error);
      return null;
    }
    return data;
  }, [user.id]);

  const showTour = student && !student.has_seen_tour && !tourDismissed;
  const showPasswordPrompt = student && !student.has_set_password && !passwordDismissed && !showTour;

  const handleTourComplete = () => {
    setTourDismissed(true);
    markTourSeen(user.id);
  };

  const handlePasswordComplete = () => {
    setPasswordDismissed(true);
    markPasswordSet(user.id);
    refetch();
  };

  // Mientras resuelve el acceso, no montamos sidebar ni cargamos el portal.
  if (accessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 size={32} className="animate-spin text-brand" />
      </div>
    );
  }

  // Solo bloqueamos ante un false explícito (true o null => flujo normal).
  if (hasAccess === false) {
    return <AccessExpired />;
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
      <main className="flex-1 lg:ml-64 min-w-0">
        <Topbar
          title={meta.title}
          subtitle={meta.subtitle}
          menuButton={<MobileMenuButton onToggle={toggleSidebar} />}
        />
        <div className="p-4 md:p-6">
          <Outlet context={{ student, refetchStudent: refetch }} />
        </div>
      </main>

      {showTour && <GuidedTour onComplete={handleTourComplete} />}
      {showPasswordPrompt && (
        <PasswordSetupPrompt
          studentHasPassword={student?.has_set_password}
          onComplete={handlePasswordComplete}
        />
      )}
    </div>
  );
}
